import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FaCogs, FaCloud, FaUsers, FaGamepad, FaDrawPolygon,
    FaShapes, FaCube, FaShoppingCart, FaVrCardboard,
    FaPalette, FaDatabase, FaCode, FaMobileAlt, FaGlobe,
    FaRocket, FaCheckCircle, FaChevronDown
} from 'react-icons/fa';

const ServicesShowcase = () => {
    const serviceCategories = [
        {
            id: 'development',
            name: 'Development',
            icon: <FaCogs className="text-blue-400" />,
            description: 'End-to-end development services from concept to deployment',
            services: [
                {
                    title: 'Mobile Development',
                    icon: <FaMobileAlt />,
                    description: 'Build seamless mobile apps for iOS and Android platforms.',
                    keywords: ['mobile app development', 'iOS apps', 'Android apps']
                },
                {
                    title: 'Web Development',
                    icon: <FaGlobe />,
                    description: 'Responsive, high-performance websites and web applications.',
                    keywords: ['web development', 'responsive design', 'web applications']
                },
                {
                    title: 'Shopify Development',
                    icon: <FaShoppingCart />,
                    description: 'Custom Shopify stores designed to convert visitors into customers.',
                    keywords: ['shopify development', 'ecommerce website']
                },
                {
                    title: 'MVP Development',
                    icon: <FaRocket />,
                    description: 'Bring your ideas to life with rapid MVP development.',
                    keywords: ['MVP development', 'product development']
                }
            ]
        },
        {
            id: 'design',
            name: 'Design',
            icon: <FaDrawPolygon className="text-purple-400" />,
            description: 'Visual storytelling through stunning designs',
            services: [
                {
                    title: 'UI/UX Design',
                    icon: <FaDrawPolygon />,
                    description: 'Craft stunning user experiences and intuitive interfaces.',
                    keywords: ['UI/UX design', 'prototyping', 'user experience']
                },
                {
                    title: 'Graphics Design',
                    icon: <FaPalette />,
                    description: 'Visual storytelling through eye-catching designs.',
                    keywords: ['graphic design', 'branding', 'visual identity']
                }
            ]
        },
        {
            id: 'gaming',
            name: 'Gaming Solutions',
            icon: <FaGamepad className="text-green-400" />,
            description: 'Immersive gaming experiences across all platforms',
            services: [
                {
                    title: '2D/3D Game Dev',
                    icon: <FaGamepad />,
                    description: 'Create engaging 2D and 3D games with stunning graphics.',
                    keywords: ['game development', '2D games', '3D games']
                },
                {
                    title: 'AR/VR Development',
                    icon: <FaVrCardboard />,
                    description: 'Immersive augmented and virtual reality experiences.',
                    keywords: ['AR development', 'VR games', 'mixed reality']
                }
            ]
        },
        {
            id: 'software',
            name: 'Software Solutions',
            icon: <FaDatabase className="text-amber-400" />,
            description: 'Custom solutions to streamline your business',
            services: [
                {
                    title: 'ERP/CRM Solutions',
                    icon: <FaDatabase />,
                    description: 'Custom systems for seamless business operations.',
                    keywords: ['ERP system', 'CRM software', 'business automation']
                },
                {
                    title: 'Custom Software',
                    icon: <FaCode />,
                    description: 'Tailored applications to automate your processes.',
                    keywords: ['custom software', 'business software']
                }
            ]
        }
    ];

    const [expandedCategory, setExpandedCategory] = useState(null);
    const [activeService, setActiveService] = useState(0);

    return (
        <section className="bg-gray-900 text-white py-16 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                {/* SEO-Optimized Heading */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500">
                            Comprehensive Digital Solutions
                        </span>
                    </h1>
                    <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                        We deliver end-to-end services tailored to your unique business needs
                    </p>
                </motion.div>

                {/* Mobile - Accordion View */}
                <div className="lg:hidden space-y-4">
                    {serviceCategories.map((category, index) => (
                        <div key={category.id} className="bg-gray-800 rounded-xl overflow-hidden">
                            <button
                                onClick={() => setExpandedCategory(expandedCategory === index ? null : index)}
                                className="w-full flex items-center justify-between p-6"
                                aria-expanded={expandedCategory === index}
                            >
                                <div className="flex items-center gap-4">
                                    <div className="text-2xl">{category.icon}</div>
                                    <h2 className="text-xl font-bold">{category.name}</h2>
                                </div>
                                <FaChevronDown className={`transition-transform ${expandedCategory === index ? 'transform rotate-180' : ''}`} />
                            </button>

                            <AnimatePresence>
                                {expandedCategory === index && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        transition={{ duration: 0.3 }}
                                        className="px-6 pb-6"
                                    >
                                        <p className="text-blue-300 mb-4">{category.description}</p>
                                        <div className="grid grid-cols-1 gap-4">
                                            {category.services.map((service, i) => (
                                                <div key={i} className="bg-gray-700/50 rounded-lg p-4">
                                                    <div className="flex items-start gap-3">
                                                        <div className="text-xl mt-1 text-blue-400">{service.icon}</div>
                                                        <div>
                                                            <h3 className="font-bold">{service.title}</h3>
                                                            <p className="text-gray-300 text-sm mt-1">{service.description}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    ))}
                </div>

                {/* Desktop - Interactive Grid */}
                <div className="hidden lg:grid grid-cols-1 md:grid-cols-2 gap-8">
                    {serviceCategories.map((category, index) => (
                        <motion.div
                            key={category.id}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ delay: index * 0.1, duration: 0.5 }}
                            className="bg-gray-800 rounded-xl overflow-hidden"
                        >
                            <div className="p-8">
                                <div className="flex items-center gap-4 mb-4">
                                    <div className="text-3xl">{category.icon}</div>
                                    <h2 className="text-2xl font-bold">{category.name}</h2>
                                </div>
                                <p className="text-blue-300 mb-6">{category.description}</p>

                                <div className="space-y-4">
                                    {category.services.map((service, i) => (
                                        <motion.div
                                            key={i}
                                            whileHover={{ x: 5 }}
                                            className="flex items-start gap-4 p-4 bg-gray-700/30 rounded-lg cursor-pointer"
                                            onClick={() => setActiveService(index * 4 + i)}
                                        >
                                            <div className="text-xl text-blue-400 mt-1">{service.icon}</div>
                                            <div>
                                                <h3 className="font-bold">{service.title}</h3>
                                                <p className="text-gray-300 text-sm mt-1">{service.description}</p>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Service Details Panel */}
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    className="mt-16 bg-gray-800 rounded-xl overflow-hidden"
                >
                    <div className="p-8">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="text-3xl text-blue-400">
                                {serviceCategories.flatMap(c => c.services)[activeService]?.icon}
                            </div>
                            <h2 className="text-2xl font-bold">
                                {serviceCategories.flatMap(c => c.services)[activeService]?.title}
                            </h2>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            <div className="lg:col-span-2">
                                <p className="text-gray-300 mb-6">
                                    {serviceCategories.flatMap(c => c.services)[activeService]?.description}
                                </p>

                                <div className="bg-gray-700/30 rounded-lg p-6">
                                    <h3 className="font-bold mb-4 flex items-center gap-2">
                                        <FaCheckCircle className="text-green-400" /> Key Features
                                    </h3>
                                    <ul className="space-y-3">
                                        {[
                                            "Cutting-edge technology stack",
                                            "Responsive design principles",
                                            "Performance optimization",
                                            "Secure and scalable architecture",
                                            "Ongoing support and maintenance"
                                        ].map((item, i) => (
                                            <li key={i} className="flex items-start gap-2">
                                                <span className="text-blue-400 mt-1">•</span>
                                                <span className="text-gray-300">{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>

                            <div className="bg-gray-700/20 rounded-lg p-6">
                                <h3 className="font-bold mb-4">Technologies We Use</h3>
                                <div className="flex flex-wrap gap-2">
                                    {[
                                        "React", "Node.js", "Python", "Django", "Flutter",
                                        "Unity", "Shopify", "AWS", "Firebase", "MongoDB"
                                    ].map((tech, i) => (
                                        <span key={i} className="bg-gray-700 text-gray-300 px-3 py-1 rounded-full text-sm">
                                            {tech}
                                        </span>
                                    ))}
                                </div>

                                <button className="mt-8 w-full py-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg font-medium hover:shadow-lg transition-all">
                                    Get a Free Consultation
                                </button>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default ServicesShowcase;